"use client";

import Image from "next/image";
import {
  BadgeCheck,
  BookOpen,
  Braces,
  Globe,
  MessageSquareQuote,
  Newspaper,
  Sparkles,
  Star,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AeoSubpageCopy } from "./aeo-page-copy";

const SOURCE_ICONS = {
  site: Globe,
  faq: MessageSquareQuote,
  schema: Braces,
  reviews: Star,
  press: Newspaper,
  directories: BookOpen,
} as const;

/**
 * Layer9-style flow: your sources on top, lines that draw themselves into
 * the answer engine as the section scrolls in, and the answer naming you
 * below. Signals then travel along the lines on a loop.
 */
export function CcAeoFlow({ copy }: { copy: AeoSubpageCopy["flow"] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOn(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -25% 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const count = copy.sources.length;

  return (
    <section
      className="cc-section cc-aeo-flow"
      id="aeo-how"
      aria-labelledby="cc-aeo-flow-title"
      data-cc-nav="light"
      {...devSectionAttr("aeo-how")}
    >
      <div className="cc-aeo-flow__container">
        <header className="cc-aeo-head">
          <p className="cc-aeo-mono">{copy.eyebrow}</p>
          <h2 id="cc-aeo-flow-title" className="cc-aeo-title">
            {copy.title}
          </h2>
          <p className="cc-aeo-intro">{copy.text}</p>
        </header>

        <div
          ref={ref}
          className="cc-aeo-flow__diagram"
          data-on={on ? "true" : undefined}
        >
          <ul className="cc-aeo-flow__sources">
            {copy.sources.map((source, index) => {
              const Icon =
                SOURCE_ICONS[source.id as keyof typeof SOURCE_ICONS] ?? Globe;
              return (
                <li key={source.id} style={{ ["--i" as string]: index }}>
                  <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
                  {source.label}
                  {source.logos.length ? (
                    <span
                      className="cc-aeo-flow__source-logos"
                      aria-hidden="true"
                    >
                      {source.logos.map((logo) => (
                        <Image
                          key={logo}
                          src={logo}
                          alt=""
                          width={14}
                          height={14}
                          unoptimized
                        />
                      ))}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <svg
            className="cc-aeo-flow__lines"
            viewBox="0 0 600 140"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {copy.sources.map((source, index) => {
              const x = ((index + 0.5) / count) * 600;
              const d = `M${x.toFixed(1)} 0 C${x.toFixed(1)} 70 300 60 300 140`;
              return (
                <g key={source.id} style={{ ["--i" as string]: index }}>
                  <path className="cc-aeo-flow__line" d={d} pathLength={1} />
                  <path className="cc-aeo-flow__pulse" d={d} pathLength={1} />
                </g>
              );
            })}
          </svg>

          <div className="cc-aeo-flow__hub">
            <span className="cc-aeo-flow__hub-icon" aria-hidden="true">
              <Sparkles size={20} strokeWidth={2} />
            </span>
            <strong>{copy.hub}</strong>
            <span className="cc-aeo-flow__engines">
              {copy.engines.map((engine) => (
                <i key={engine.id}>
                  <Image
                    src={engine.logo}
                    alt=""
                    width={13}
                    height={13}
                    unoptimized
                  />
                  {engine.name}
                </i>
              ))}
            </span>
          </div>

          <span className="cc-aeo-flow__stem" aria-hidden="true" />

          <div className="cc-aeo-flow__result">
            <BadgeCheck size={22} strokeWidth={2} aria-hidden="true" />
            <span>
              <strong>{copy.result.title}</strong>
              <small>{copy.result.text}</small>
            </span>
          </div>
        </div>

        <div className="cc-aeo-grid">
          <p className="cc-aeo-grid__title">{copy.grid.title}</p>
          {[
            {
              key: "engines",
              label: copy.grid.enginesLabel,
              items: copy.grid.engines,
            },
            {
              key: "sources",
              label: copy.grid.sourcesLabel,
              items: copy.grid.sources,
            },
          ].map((group) => (
            <div key={group.key} className="cc-aeo-grid__group">
              <span className="cc-aeo-mono">{group.label}</span>
              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <Image
                      src={item.logo}
                      alt=""
                      width={26}
                      height={26}
                      unoptimized
                    />
                    <span>{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
